/**
 * Onboarding page 2 — « Votre marché ».
 *
 * Four cards, prefilled: Thèmes d'autorité (`keywords`), Cas d'usage (`scenarios`), Critères
 * d'évaluation (`decisionCriteria`) and Concurrents (the GEO Strategy's `competitiveFocus`).
 * Each ⓘ says which prompts the list generates and groups (the Prompt Studio vocabulary).
 * « Options avancées » → personas (Brand Knowledge personas, same gate as that page).
 *
 * The GEO Strategy review is gone (owner 2026-11-03: only the competitors matter). It is still
 * generated on entry and validated on save, because the strategy gate needs BOTH layers.
 * On entry the Cibles queued on page 1 are written (pendingTargetsCache).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, Card, CircularProgress, IconButton, InputBase } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BalanceIcon from '@mui/icons-material/Balance';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import type { Brand, BrandQueryAxis } from '@traaker/shared';
import { BRAND_STRATEGY_LIMITS, effectiveFeatureFlags, resolveEntitlement, resolveLimits } from '@traaker/shared';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { competitorsApi } from '../../services/competitors';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { useDomainFavicon } from '../../hooks/useDomainFavicon';
import PersonasManager from '../../components/brand/knowledge/PersonasManager';
import { AXIS_COLOR } from '../../components/prompt-studio/promptStudioModel';
import {
  AdvancedToggle,
  ChoiceList,
  InfoTip,
  QuitOnboardingButton,
  SubCard,
  competitorNameFromDomain,
  domainIfLooksLikeOne,
  isValidCompetitorDomain,
  normalizeCompetitorDomain,
  type ChoiceItem,
} from './onboardingShared';
import { pendingTargetsCache, runPendingTargets, type PendingResult } from './pendingTargets';

/** Discovered items pre-checked per list; the rest are listed unchecked (no cap: experts keep them all). */
const PRESELECT = 5;
/** Competitors listed / pre-checked (owner 2026-11-04: 8 was too many; each tracked one also seeds Comparaison prompts). */
const COMPETITOR_PROPOSE_MAX = 5;
const COMPETITOR_PRESELECT = 3;

interface Competitor {
  name: string;
  domain: string | null;
  track: boolean;
}

const preselected = (xs?: string[]): ChoiceItem[] => (xs ?? []).map((text, i) => ({ text, on: i < PRESELECT }));
const kept = (xs: ChoiceItem[]) => xs.filter((x) => x.on).map((x) => x.text);

export function MarketStep({
  brandId,
  brand,
  onDone,
  onSaveQuit,
  canQuit,
  onQuit,
}: {
  brandId: string;
  brand: Brand | null;
  onDone: () => Promise<void>;
  /** « Enregistrer et quitter »: everything saved, onboarding marked complete by the caller. */
  onSaveQuit: () => Promise<void>;
  canQuit: boolean;
  onQuit: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const { refreshBrands } = useBrand();
  const competitorLimit = tenant
    ? resolveEntitlement('competitors', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.competitors,
        packs: tenant.settings?.activePacks?.competitors,
      }).limit
    : -1;
  // Personas are a Conversations asset: same gate as Brand Knowledge (flag + conversation_personas ≠ 0).
  const showPersonas =
    !!tenant &&
    !!effectiveFeatureFlags(tenant.tier, tenant.settings?.featureFlags).conversationsEnabled &&
    resolveLimits(tenant.tier, tenant.settings?.quotaOverrides).conversation_personas !== 0;
  const personasLimit = tenant
    ? resolveEntitlement('brand_personas', tenant.tier, {
        override: (tenant.settings?.quotaOverrides as Record<string, number> | undefined)?.brand_personas,
      }).limit
    : undefined;

  const strategy = brand?.strategy;
  const geo = brand?.geoStrategy;
  const hasGeo = !!geo && Object.keys(geo).length > 0;

  const [themes, setThemes] = useState<ChoiceItem[]>(() => preselected(strategy?.keywords));
  const [usecases, setUsecases] = useState<ChoiceItem[]>(() => preselected(strategy?.scenarios));
  const [criteria, setCriteria] = useState<ChoiceItem[]>(() => preselected(strategy?.decisionCriteria));
  // The brand row read right after page 1's strategy save can lag: seed the lists when the
  // strategy arrives, as long as the user has not edited them yet.
  const editedRef = useRef(false);
  const seededRef = useRef(!!(strategy?.keywords?.length || strategy?.scenarios?.length || strategy?.decisionCriteria?.length));
  useEffect(() => {
    if (seededRef.current || editedRef.current) return;
    if (!strategy?.keywords?.length && !strategy?.scenarios?.length && !strategy?.decisionCriteria?.length) return;
    seededRef.current = true;
    setThemes(preselected(strategy?.keywords));
    setUsecases(preselected(strategy?.scenarios));
    setCriteria(preselected(strategy?.decisionCriteria));
  }, [strategy?.keywords, strategy?.scenarios, strategy?.decisionCriteria]);
  const edit = (set: (xs: ChoiceItem[]) => void) => (xs: ChoiceItem[]) => {
    editedRef.current = true;
    set(xs);
  };
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [competitorInput, setCompetitorInput] = useState('');
  const [advOpen, setAdvOpen] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'continue' | 'quit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [targetResults, setTargetResults] = useState<PendingResult[] | null>(null);

  // Cibles queued on page 1: written once, right after creation.
  const targetsRanRef = useRef(false);
  useEffect(() => {
    if (targetsRanRef.current) return;
    targetsRanRef.current = true;
    const queue = pendingTargetsCache.get(brandId);
    pendingTargetsCache.delete(brandId);
    if (!queue?.length) return;
    setTargetResults(queue.map((target) => ({ target, ok: false })));
    void runPendingTargets(brandId, queue).then(setTargetResults);
  }, [brandId]);

  const generate = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    try {
      await api.post(`/brands/${brandId}/geo-strategy/generate`, {});
      await refreshBrands();
    } catch (err: any) {
      setGenError(err.message || t('onboarding.geo.generateError'));
    } finally {
      setGenerating(false);
    }
  }, [brandId, refreshBrands, t]);

  // GEO Strategy generated once on entry (a resumed brand may already have one).
  useEffect(() => {
    if (!hasGeo && !generating) void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the GEO lands: the competitor list (the brand itself filtered out, domains resolved in the background).
  useEffect(() => {
    if (!hasGeo || !geo) return;
    const ownDomain = domainIfLooksLikeOne(brand?.url || '');
    const ownName = (brand?.name || '').trim().toLowerCase();
    const isSelf = (name: string) =>
      (ownName && name.trim().toLowerCase() === ownName) || (!!ownDomain && domainIfLooksLikeOne(name) === ownDomain);
    const preselectMax = competitorLimit === -1 ? COMPETITOR_PRESELECT : Math.min(competitorLimit, COMPETITOR_PRESELECT);
    const focus = (geo.competitiveFocus || []).filter((n) => !isSelf(n)).slice(0, COMPETITOR_PROPOSE_MAX);
    setCompetitors(focus.map((name, i) => ({ name, domain: domainIfLooksLikeOne(name), track: i < preselectMax })));
    focus.forEach((name) => {
      if (domainIfLooksLikeOne(name)) return;
      competitorsApi
        .resolveDomain(brandId, name)
        .then((res) => {
          if (!res.domain) return;
          if (ownDomain && res.domain === ownDomain) {
            setCompetitors((prev) => prev.filter((c) => c.name !== name));
            return;
          }
          setCompetitors((prev) => prev.map((c) => (c.name === name ? { ...c, domain: res.domain } : c)));
        })
        .catch(() => void 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGeo]);

  const tracked = competitors.filter((c) => c.track).length;
  const atCap = competitorLimit !== -1 && tracked >= competitorLimit;

  const toggleCompetitor = (i: number) =>
    setCompetitors((prev) => prev.map((c, j) => (j !== i ? c : !c.track && atCap ? c : { ...c, track: !c.track })));

  // Manual add is DOMAIN-FIRST (a name-only entry too often resolves to the wrong site).
  const addCompetitor = () => {
    const domain = normalizeCompetitorDomain(competitorInput);
    setCompetitorInput('');
    if (!isValidCompetitorDomain(domain)) return;
    if (domain === domainIfLooksLikeOne(brand?.url || '')) return;
    if (competitors.some((c) => c.domain === domain)) return;
    setCompetitors((prev) => [...prev, { name: competitorNameFromDomain(domain), domain, track: !atCap }]);
  };

  /** The lists → Brand Strategy (re-validated: a PATCH flips it), the GEO validated, competitors tracked. */
  const save = async () => {
    await api.patch(`/brands/${brandId}/strategy`, {
      keywords: kept(themes),
      scenarios: kept(usecases),
      decisionCriteria: kept(criteria),
    });
    await api.post(`/brands/${brandId}/strategy/validate`, {});
    await api.post(`/brands/${brandId}/geo-strategy/validate`, {});
    // A real domain is required server-side: an unresolved competitor is not tracked.
    await Promise.allSettled(
      competitors
        .filter((c) => c.track && c.domain && isValidCompetitorDomain(c.domain))
        .map((c) => competitorsApi.add(brandId, { name: c.name, domain: c.domain! })),
    );
  };

  const handle = async (which: 'continue' | 'quit') => {
    setSaving(which);
    setError(null);
    try {
      await save();
      await (which === 'continue' ? onDone() : onSaveQuit());
    } catch (err: any) {
      setError(err.message || t('onboarding.geo.validateError'));
      setSaving(null);
    }
  };

  const failedTargets = targetResults?.filter((r) => !r.ok && r.error) ?? [];
  const runningTargets = !!targetResults && targetResults.some((r) => !r.ok && !r.error);

  if (generating && !hasGeo) {
    return (
      <Card sx={{ borderRadius: 4, py: 9, textAlign: 'center' }}>
        <CircularProgress size={32} sx={{ mb: 2 }} />
        <Box sx={{ fontWeight: 700, fontSize: 19 }}>{t('onboarding.market.analyzing', { name: brand?.name || '' })}</Box>
      </Card>
    );
  }

  return (
    <Box>
      {genError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => void generate()}>{t('onboarding.prompts.review.retry')}</Button>}>
          {genError}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {runningTargets && <Alert severity="info" sx={{ mb: 2 }}>{t('onboarding.market.targetsRunning')}</Alert>}
      {failedTargets.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('onboarding.market.targetsFailed', { count: failedTargets.length })} {failedTargets.map((r) => r.error).join(' · ')}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <ListCard
          icon={<SchoolOutlinedIcon sx={{ fontSize: 18 }} />}
          tint="#ecfdf5"
          title={t('onboarding.market.themes')}
          info={<CardInfo axes={['authority']} gen={t('onboarding.market.info.themes')} group={t('onboarding.market.info.groupTheme')} />}
          items={themes}
          onChange={edit(setThemes)}
          maxChars={BRAND_STRATEGY_LIMITS.keywords.maxCharsPerItem}
        />
        <ListCard
          icon={<TrackChangesIcon sx={{ fontSize: 18 }} />}
          tint="#fff7ed"
          title={t('onboarding.market.usecases')}
          info={<CardInfo axes={['referral']} gen={t('onboarding.market.info.usecases')} group={t('onboarding.market.info.groupUsecase')} />}
          items={usecases}
          onChange={edit(setUsecases)}
          maxChars={BRAND_STRATEGY_LIMITS.scenarios.maxCharsPerItem}
        />
        <ListCard
          icon={<BalanceIcon sx={{ fontSize: 18 }} />}
          tint="#eff6ff"
          title={t('onboarding.market.criteria')}
          info={<CardInfo axes={['narrative', 'comparison']} gen={t('onboarding.market.info.criteria')} group={t('onboarding.market.info.groupCriterion')} />}
          items={criteria}
          onChange={edit(setCriteria)}
          maxChars={BRAND_STRATEGY_LIMITS.decisionCriteria.maxCharsPerItem}
        />
        <Card sx={{ borderRadius: 4, p: 2.5 }}>
          <CardTitle
            icon={<SportsMmaIcon sx={{ fontSize: 18 }} />}
            tint="#fdf2f8"
            title={t('onboarding.market.competitors')}
            info={<CardInfo axes={['comparison']} gen={t('onboarding.market.info.competitors')} group={t('onboarding.market.info.groupCompetitor')} />}
            count={competitorLimit === -1 ? `${tracked}` : `${tracked} / ${competitorLimit}`}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {competitors.map((c, i) => (
              <CompetitorTile key={`${c.name}-${i}`} c={c} onToggle={() => toggleCompetitor(i)} onRemove={() => setCompetitors((prev) => prev.filter((_, j) => j !== i))} />
            ))}
            <Box sx={{ px: 1.4, py: 0.75, borderRadius: 2.5, border: '1px dashed', borderColor: 'divider' }}>
              <InputBase
                fullWidth
                value={competitorInput}
                onChange={(e) => setCompetitorInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCompetitor(); } }}
                placeholder={t('onboarding.market.competitorPlaceholder')}
                sx={{ fontSize: 13 }}
              />
            </Box>
          </Box>
        </Card>
      </Box>

      <Card sx={{ borderRadius: 4, mt: 2 }}>
        {showPersonas && (
          <AdvancedToggle open={advOpen} onToggle={() => setAdvOpen((o) => !o)}>
            <SubCard
              icon={<GroupsOutlinedIcon sx={{ fontSize: 17 }} />}
              title={t('onboarding.market.personas')}
              info={t('onboarding.market.info.personas')}
            >
              <PersonasManager brandId={brandId} limit={personasLimit} />
            </SubCard>
          </AdvancedToggle>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 4, py: 2.25, borderTop: showPersonas ? '1px solid' : 'none', borderColor: 'divider' }}>
          {canQuit && <QuitOnboardingButton brandName={brand?.name} onQuit={onQuit} />}
          <Box sx={{ flex: 1 }} />
          <Button variant="text" onClick={() => void handle('quit')} disabled={!!saving || !hasGeo}>
            {saving === 'quit' ? <CircularProgress size={18} /> : t('onboarding.market.saveQuit')}
          </Button>
          <Button variant="contained" size="large" onClick={() => void handle('continue')} disabled={!!saving || !hasGeo} sx={{ px: 4, borderRadius: 3 }}>
            {saving === 'continue' ? <CircularProgress size={20} color="inherit" /> : t('onboarding.common.continue')}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}

const AXIS_KEY: Record<BrandQueryAxis, string> = {
  narrative: 'onboarding.axis.narrative',
  authority: 'onboarding.axis.authority',
  referral: 'onboarding.axis.referral',
  comparison: 'onboarding.axis.comparison',
};

/** The ⓘ body: which prompts the list generates, how they are grouped, the axes it feeds. */
function CardInfo({ axes, gen, group }: { axes: BrandQueryAxis[]; gen: string; group: string }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ fontSize: 12, lineHeight: 1.5, p: 0.25 }}>
      <Box>{gen}</Box>
      <Box sx={{ mt: 0.75, opacity: 0.85 }}>{group}</Box>
      <Box sx={{ display: 'flex', gap: 1.25, mt: 0.75 }}>
        {axes.map((a) => (
          <Box key={a} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: AXIS_COLOR[a] }} />
            {t(AXIS_KEY[a])}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function CardTitle({ icon, tint, title, info, count }: { icon: React.ReactNode; tint: string; title: string; info: React.ReactNode; count: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 1.75 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: 2.25, display: 'grid', placeItems: 'center', bgcolor: tint, color: 'text.secondary' }}>{icon}</Box>
      <Box sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Box>
      <InfoTip title={info} />
      <Box sx={{ ml: 'auto', fontSize: 11.5, color: 'text.disabled', fontWeight: 500 }}>{count}</Box>
    </Box>
  );
}

function ListCard({
  icon,
  tint,
  title,
  info,
  items,
  onChange,
  maxChars,
}: {
  icon: React.ReactNode;
  tint: string;
  title: string;
  info: React.ReactNode;
  items: ChoiceItem[];
  onChange: (items: ChoiceItem[]) => void;
  maxChars: number;
}) {
  return (
    <Card sx={{ borderRadius: 4, p: 2.5 }}>
      <CardTitle icon={icon} tint={tint} title={title} info={info} count={`${items.filter((i) => i.on).length}`} />
      <ChoiceList items={items} onChange={onChange} selectable maxChars={maxChars} />
    </Card>
  );
}

function CompetitorTile({ c, onToggle, onRemove }: { c: Competitor; onToggle: () => void; onRemove: () => void }) {
  const { t } = useTranslation();
  const favicon = useDomainFavicon(c.domain || undefined, 64);
  // Trackable only once its real domain is resolved (the backend requires one).
  const ok = !!c.domain;
  const on = c.track && ok;
  return (
    <Box
      onClick={ok ? onToggle : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.1, px: 1.4, py: 0.9, borderRadius: 2.5, fontSize: 13,
        border: '1px solid', borderColor: on ? 'rgba(124,58,237,.22)' : 'divider', bgcolor: on ? '#faf8ff' : 'background.paper',
        cursor: ok ? 'pointer' : 'default', opacity: ok ? 1 : 0.6, '&:hover .comp-del': { visibility: 'visible' },
      }}
    >
      <Box sx={{
        width: 18, height: 18, borderRadius: 1.25, flexShrink: 0, display: 'grid', placeItems: 'center', color: '#fff',
        border: '1.5px solid', borderColor: on ? 'primary.main' : '#d4d4d8', bgcolor: on ? 'primary.main' : 'transparent',
      }}>
        {on && <CheckIcon sx={{ fontSize: 13 }} />}
      </Box>
      <Avatar src={favicon || undefined} variant="rounded" sx={{ width: 22, height: 22, fontSize: 11, bgcolor: 'action.hover' }}>
        {c.name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</Box>
        <Box sx={{ fontSize: 11.5, color: 'text.disabled' }}>{c.domain || t('onboarding.geo.competitors.resolvingDomain')}</Box>
      </Box>
      <IconButton
        className="comp-del"
        size="small"
        aria-label={t('onboarding.common.remove')}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        sx={{ visibility: 'hidden', p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
      >
        <CloseIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
}
